import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import { Button, Flex, Form, Input, InputNumber, Tooltip, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdvancedTable, {
  AddButton,
  AdvancedTableRef,
  getActionColumns,
} from "../components/AdvancedTable";
import {
  KeyValueDataContexMap,
  useKeyValueData,
} from "../contexts/KeyValueDataProvider";
import { DictItem } from "../shared/types/DictItem";
import { dataCall } from "../tsrpc";

const { Title } = Typography;

export interface PageKeyValueProps {
  title: string;
  collection: keyof typeof KeyValueDataContexMap;
  backText?: string;
  onBack?: () => void;
  closeButton?: boolean;
}

export default function PageKeyValue({
  title,
  collection,
  backText,
  onBack,
  closeButton,
}: PageKeyValueProps) {
  const navigate = useNavigate();
  const tableRef = useRef<AdvancedTableRef<DictItem>>(null);
  const [editingData, setEditingData] = useState<DictItem | undefined>();
  const context = useKeyValueData(collection);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    getVersion();
  }, []);

  const getVersion = async () => {
    const res = await dataCall("editor/DataFind", {
      collection: "versions",
      filter: { collection },
      limit: 1,
    });
    setVersion(res.res?.data?.[0]?.version ?? 0);
  };

  return (
    <AdvancedTable<DictItem>
      ref={tableRef}
      editingData={editingData}
      title={() => (
        <Flex justify="space-between" align="center">
          <Flex gap={10}>
            <Title level={5}>{title}</Title>
            <p>{`[数据版本：${version}]`}</p>
          </Flex>
          <Flex gap={10}>
            <AddButton<DictItem>
              title="新增"
              add={() => context.add}
              createValue={() => {
                let value = 1;
                while (context.data.some((item) => item.value === value))
                  value++;
                return { value };
              }}
            >
              <Form.Item
                name="value"
                label="值"
                rules={[
                  { required: true, message: "请输入值！" },
                  {
                    validator: (_r, v) => {
                      console.log(context.data);
                      return v && context.data.some((item) => item.value === v)
                        ? Promise.reject("值已存在！")
                        : Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber />
              </Form.Item>
              <Form.Item
                name="label"
                label="名称"
                rules={[{ required: true, message: "请输入名称！" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="alias" label="别名">
                <Input />
              </Form.Item>
              <Form.Item name="description" label="备注">
                <Input.TextArea />
              </Form.Item>
            </AddButton>
            {closeButton ? (
              <Tooltip title="关闭">
                <Button
                  icon={<CloseOutlined />}
                  onClick={onBack ? onBack : () => navigate("/control/props")}
                />
              </Tooltip>
            ) : (
              <Button
                icon={<LeftOutlined />}
                onClick={onBack ? onBack : () => navigate("/control/props")}
              >
                {backText}
              </Button>
            )}
          </Flex>
        </Flex>
      )}
      columns={[
        {
          title: "值",
          dataIndex: "value",
          width: 200,
          editor: <InputNumber />,
          formItemProps: {
            rules: [
              { required: true, message: "请输入值！" },
              {
                validator: (_r, v) => {
                  console.log(context.data);
                  return v && context.data.some((item) => item.value === v)
                    ? Promise.reject("值已存在！")
                    : Promise.resolve();
                },
              },
            ],
          },
        },
        {
          title: "名称",
          dataIndex: "label",
          width: 200,
          editor: <Input />,
          formItemProps: {
            rules: [{ required: true, message: "请输入名称！" }],
          },
        },
        {
          title: "别名",
          dataIndex: "alias",
          width: 200,
          editor: <Input />,
        },
        {
          title: "备注",
          dataIndex: "description",
          editor: <Input.TextArea />,
        },
        getActionColumns({
          editingData,
          setEditingData,
          display: (d) => `${d?.value}:${d?.label}`,
          editSubmit: () => tableRef.current?.editSubmit,
          $delete: () => tableRef.current?.delete,
        }),
      ]}
      context={context}
    />
  );
}
